import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { userSelector } from 'redux/selectors';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useIntroPathCard } from './hooks/useIntroPathCard';
import { useHasRelationshipStrengthFeature } from '../../../hooks/useHasRelationshipStrengthFeature';
import { PersonBlock } from './components/personBlock';
import { Connector } from './components/connector';
import { LegacyCard } from './components/legacyCard';
import RelationshipStrengthInfoMessage from '../../molecules/RelationshipStrengthInfoMessage';
import RelationshipStrengthFeedback from '../../molecules/RelationshipStrengthFeedback';
import { trackEvent } from '../../../helpers/analytics';
import {
  CardWrap,
  CriterionIcon,
  CriterionRow,
  CriterionText,
  DetailsToggle,
  FooterPanel,
  LegLabel,
  LevelEmphasis,
  Row,
  SignalsTitle,
} from './IntroPathCard.styles';

const firstNameOf = (name) => (name || '').trim().split(/\s+/)[0] || '';

const signalsTitleFor = (level, hasCriteria, { originName, targetName } = {}) => {
  if (level === 'inferred') {
    return originName && targetName ? (
      <>
        We inferred that {originName} and {targetName} are connected based on:
      </>
    ) : (
      <>We inferred this connection from public signals:</>
    );
  }
  if (level === 'unknown') {
    return hasCriteria ? (
      <>Connection signals:</>
    ) : (
      <>This connection was added manually. We haven&apos;t found signals on email, calendar, or LinkedIn.</>
    );
  }
  // A cold path can still carry per-leg signals (a warm leg, or BE met_criteria);
  // when criteria rows are listed below, don't head them with a "no signals" line.
  if (level === 'cold') {
    return hasCriteria ? <>Connection signals:</> : <>No meaningful signals of a direct connection.</>;
  }
  const displayedLevel = `${level.charAt(0).toUpperCase()}${level.slice(1)}`;
  return (
    <>
      Signals of a <LevelEmphasis>{displayedLevel}</LevelEmphasis> connection:
    </>
  );
};

export const IntroPathCard = ({ network, item, contact, expanded: expandedProp, onExpandedChange }) => {
  const { nodesForRender, strength } = useIntroPathCard({ item });
  const user = useSelector(userSelector);
  const currentUserId = user?.id;
  const hasRelationshipStrength = useHasRelationshipStrengthFeature({ network });
  const [internalExpanded, setInternalExpanded] = useState(false);
  const isControlled = expandedProp !== undefined;
  const expanded = isControlled ? expandedProp : internalExpanded;

  const handleContactClick = (contactId) => {
    trackEvent('contacts:intro_paths:contact_profile_clicked', { contactId, collectionId: network?.id });
  };

  // The feature flag is the single gate for the enriched UI: even if the API
  // emits a `level`, a flag-off collection stays on the legacy view.
  if (!strength || !hasRelationshipStrength) {
    return <LegacyCard network={network} nodesForRender={nodesForRender} onContactClick={handleContactClick} />;
  }

  const setExpanded = (next) => {
    if (!isControlled) setInternalExpanded(next);
    if (onExpandedChange) onExpandedChange(next);
  };

  const toggleExpanded = () => {
    const next = !expanded;
    setExpanded(next);
    trackEvent('contacts:intro_paths:path_toggled', {
      contactId: contact.id,
      pathId: item?.id,
      action: next ? 'expand' : 'collapse',
    });
  };

  const personNodes = nodesForRender.filter((n) => n.kind !== 'signal');
  // A path needs at least an origin + a target. Anything shorter is malformed —
  // bail rather than dereference a missing second person.
  if (personNodes.length < 2) return null;

  const originPathNode = (item?.path || []).find((n) => n?.kind === 'user');
  const isOriginCurrentUser =
    !!originPathNode &&
    (String(originPathNode.id) === String(currentUserId) ||
      String(originPathNode.user?.data?.id) === String(currentUserId));
  const audience = isOriginCurrentUser ? 'current-user' : 'team-member';

  // The connect/copy nudge fires per missing source. For the current user we
  // read straight off the Redux user object (same source the integrations page
  // uses): Gmail and Calendar are derived per-source from the first account's
  // sync toggles, so no account means both are missing. For a team-member
  // origin we rely on the BE flags on the node (camelCased, snake_case fallback)
  // since we can't see that teammate's integration state; the node carries no
  // calendar flag today, so team-member calendar stays coarse (never flagged).
  const currentUserGoogleAccount = user?.googleSyncAccounts?.[0];
  const currentUserMissingLinkedIn = !user?.linkedinSync;
  const currentUserMissingEmail = !currentUserGoogleAccount?.emailSyncEnabled;
  const currentUserMissingCalendar = !currentUserGoogleAccount?.calendarSyncEnabled;
  const missingLinkedIn = isOriginCurrentUser
    ? currentUserMissingLinkedIn
    : (originPathNode?.linkedinConnected ?? originPathNode?.linkedin_connected) === false;
  const missingEmail = isOriginCurrentUser
    ? currentUserMissingEmail
    : (originPathNode?.emailConnected ?? originPathNode?.email_connected) === false;
  const missingCalendar = isOriginCurrentUser ? currentUserMissingCalendar : false;
  const originFirstName = firstNameOf(personNodes[0]?.avatarName);
  const targetFirstName = firstNameOf(personNodes[personNodes.length - 1]?.avatarName);

  const pillFor = (index) => {
    if (index === 0) return isOriginCurrentUser ? 'you' : 'team';
    if (index === personNodes.length - 1) return null; // target gets no pill
    return 'contact'; // any intermediate hop
  };

  const renderPerson = (node, index) => {
    const isTarget = index === personNodes.length - 1;
    const showEmail = expanded && node.emails?.length > 0;
    return (
      <PersonBlock
        key={node.key}
        node={node}
        network={network}
        pillVariant={pillFor(index)}
        emails={showEmail ? node.emails : undefined}
        emailFirstName={showEmail ? firstNameOf(node.avatarName) : undefined}
        onContactClick={handleContactClick}
        showProfileLink={node.kind === 'contact' && !isTarget}
        onEmailSelect={() => trackEvent('contacts:intro_paths:cta_clicked', { contactId: contact.id, cta: 'email' })}
      />
    );
  };

  return (
    <CardWrap data-path-id={item?.id} $clickable={false}>
      <Row $columns={personNodes.length}>
        {personNodes.map((node, index) => (
          <React.Fragment key={node.key}>
            {renderPerson(node, index)}
            {index < personNodes.length - 1 && <Connector level={strength.legLevels?.[index] ?? strength.level} />}
          </React.Fragment>
        ))}
      </Row>

      {expanded && (
        <FooterPanel data-testid="rs-footer">
          <SignalsTitle>
            {signalsTitleFor(strength.level, strength.metCriteria.length > 0, {
              originName: originFirstName,
              targetName: targetFirstName,
            })}
          </SignalsTitle>
          {strength.metCriteriaGroups.map((group) => (
            <React.Fragment key={`${group.from}-${group.to}`}>
              {strength.metCriteriaGroups.length > 1 && (
                <LegLabel>
                  {firstNameOf(group.from)} &amp; {firstNameOf(group.to)}
                </LegLabel>
              )}
              {group.criteria.map((criterion) => (
                <CriterionRow key={`${group.from}-${group.to}-${criterion}`}>
                  <CriterionIcon aria-hidden="true">
                    <Check size={16} strokeWidth={2} />
                  </CriterionIcon>
                  <CriterionText>{criterion}</CriterionText>
                </CriterionRow>
              ))}
            </React.Fragment>
          ))}
          {(missingLinkedIn || missingEmail || missingCalendar) && (
            <RelationshipStrengthInfoMessage
              audience={audience}
              missingLinkedIn={missingLinkedIn}
              missingEmail={missingEmail}
              missingCalendar={missingCalendar}
              firstName={originFirstName}
              networkSlug={network.slug}
              onConnectClick={(action) =>
                trackEvent('contacts:intro_paths:cta_clicked', { contactId: contact.id, cta: action })
              }
            />
          )}
          <RelationshipStrengthFeedback
            contactId={contact.id}
            pathId={item?.id != null ? String(item.id) : ''}
            leg="primary"
            relationshipStrength={strength.level}
            signalsMet={strength.metCriteria}
            onTrack={trackEvent}
          />
        </FooterPanel>
      )}

      <DetailsToggle type="button" aria-expanded={expanded} onClick={toggleExpanded}>
        <span>{expanded ? 'See less' : 'See more details'}</span>
        {expanded ? (
          <ChevronUp size={16} strokeWidth={2} aria-hidden="true" />
        ) : (
          <ChevronDown size={16} strokeWidth={2} aria-hidden="true" />
        )}
      </DetailsToggle>
    </CardWrap>
  );
};

IntroPathCard.propTypes = {
  network: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  item: PropTypes.object.isRequired,
  contact: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  }).isRequired,
  expanded: PropTypes.bool,
  onExpandedChange: PropTypes.func,
};

IntroPathCard.defaultProps = {
  expanded: undefined,
  onExpandedChange: undefined,
};
